// packages/ui/src/notify/ticketNotify.ts
//
// dbg-20260705-ticket-realtime-notify — shared browser attention primitives for
// realtime support-ticket alerts: desktop Notification, sound, and a tab
// title-flash. Used by admin / merchant / vendor.
//
// Pure mechanism — ALL user-facing text is passed in by the caller (which owns
// its i18n `t()`), so this file carries no hardcoded copy. The sound URL is a
// parameter because each app ships the chime at a different public path.


/** Ask for desktop-notification permission (no-op if already decided). */
export async function ensureNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/**
 * Show a desktop notification. `title`/`body` are already localized by the
 * caller. Clicking focuses this tab and invokes `onClick` (e.g. navigate).
 */
export function showDesktopNotification(
  title: string,
  opts: { body?: string; tag?: string; icon?: string; onClick?: () => void } = {},
): void {
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    Notification.permission !== 'granted'
  ) {
    return;
  }
  try {
    const n = new Notification(title, {
      body: opts.body,
      tag: opts.tag,
      icon: opts.icon ?? '/favicon.ico',
    });
    n.onclick = () => {
      window.focus();
      opts.onClick?.();
      n.close();
    };
  } catch {
    /* some browsers throw if constructed outside a user gesture — ignore */
  }
}

/**
 * No-op (dbg-20260710): notification SOUND was dropped entirely — attention is
 * now visual only (desktop Notification + tab-title flash/badge). Kept as an
 * exported stub so existing callers don't need to change and the sound can be
 * reinstated in one place if ever wanted. Args accepted + ignored.
 */
export function playTicketSound(
  _url?: string,
  _opts: { strong?: boolean } = {},
): void {
  /* sound intentionally disabled */
}

// --- Tab title flash --------------------------------------------------------

let flashTimer: ReturnType<typeof setInterval> | null = null;
let originalTitle: string | null = null;
let focusBound = false;
let alertIconUrl: string | null = null;
let faviconFlashed = false;

/**
 * 64px red-circle + white speaker glyph — mirrors Chrome's per-tab audio
 * indicator the user pointed at as the desired alert look
 * (dbg-20260706-183149 Image #17). Drawn once, cached as a data URL.
 */
function buildAlertFavicon(): string | null {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.beginPath();
  ctx.arc(32, 32, 30, 0, Math.PI * 2);
  ctx.fillStyle = '#ef4444';
  ctx.fill();
  // speaker body
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(13, 26);
  ctx.lineTo(21, 26);
  ctx.lineTo(31, 17);
  ctx.lineTo(31, 47);
  ctx.lineTo(21, 38);
  ctx.lineTo(13, 38);
  ctx.closePath();
  ctx.fill();
  // sound waves
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(33, 32, 9, -Math.PI / 3.2, Math.PI / 3.2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(33, 32, 16, -Math.PI / 3.2, Math.PI / 3.2);
  ctx.stroke();
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/** Swap the favicon to the red-speaker alert icon (flash companion). */
function setAlertFavicon(): void {
  const link = getFaviconLink();
  if (!link) return;
  if (faviconBase === null) faviconBase = link.href;
  alertIconUrl ??= buildAlertFavicon();
  if (!alertIconUrl) return;
  link.href = alertIconUrl;
  faviconFlashed = true;
}

function restoreFavicon(): void {
  if (!faviconFlashed) return;
  const link = getFaviconLink();
  if (link && faviconBase !== null) link.href = faviconBase;
  faviconFlashed = false;
}

function stopTitleFlash(): void {
  if (flashTimer) {
    clearInterval(flashTimer);
    flashTimer = null;
  }
  if (originalTitle !== null) {
    document.title = originalTitle;
    originalTitle = null;
  }
  restoreFavicon();
}

/**
 * Flash `message` in the tab title, alternating with the original title, until
 * the tab regains focus. Safe to call repeatedly (restarts the flash message).
 */
export function flashTitle(message: string): void {
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'visible' && document.hasFocus()) return;

  if (originalTitle === null) originalTitle = document.title;
  if (flashTimer) clearInterval(flashTimer);
  setAlertFavicon();

  let toggle = false;
  flashTimer = setInterval(() => {
    document.title = toggle ? (originalTitle ?? message) : message;
    toggle = !toggle;
  }, 1000);

  if (!focusBound) {
    focusBound = true;
    window.addEventListener('focus', () => stopTitleFlash());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') stopTitleFlash();
    });
  }
}

export function clearTitleFlash(): void {
  stopTitleFlash();
}

// --- Tab-title unread badge --------------------------------------------------

let titleBase: string | null = null;

/**
 * Prefix the tab title with the unread count — `(N) <base title>`, `99+` cap,
 * count 0 restores the bare base title. The base is captured from
 * `document.title` on first call (any existing `(N)` prefix stripped), so each
 * app keeps its own name without passing it around. Plays nice with
 * flashTitle(): while a flash is running the flash's restore target is updated
 * instead, so stopping the flash lands on the fresh count.
 */
export function setTitleUnreadBadge(count: number): void {
  if (typeof document === 'undefined') return;
  if (titleBase === null) {
    titleBase = document.title.replace(/^\(\d+\+?\)\s*/, '');
  }
  const label = count > 99 ? '99+' : String(count);
  const next = count > 0 ? `(${label}) ${titleBase}` : titleBase;
  if (originalTitle !== null) {
    // flash in progress — retarget its restore title, the interval picks it up
    originalTitle = next;
  } else {
    document.title = next;
  }
}

// --- Favicon unread badge ----------------------------------------------------

let faviconBase: string | null = null;
let faviconLink: HTMLLinkElement | null = null;
let lastBadgeCount = -1;

function getFaviconLink(): HTMLLinkElement | null {
  if (faviconLink?.isConnected) return faviconLink;
  faviconLink = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  return faviconLink;
}

/**
 * Overlay a red unread badge on the tab favicon (count ≤ 9 renders the
 * number, above that a plain dot). `count <= 0` restores the original icon.
 * Best-effort: missing <link rel="icon">, canvas taint, or load failure
 * leaves the favicon untouched.
 */
export function setFaviconBadge(count: number): void {
  if (typeof document === 'undefined') return;
  if (count === lastBadgeCount) return;
  const link = getFaviconLink();
  if (!link) return;
  if (faviconBase === null) faviconBase = link.href;

  if (count <= 0) {
    link.href = faviconBase;
    lastBadgeCount = 0;
    return;
  }
  lastBadgeCount = count;

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const size = 64;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, size, size);

      // Red badge, top-right, white ring so it reads on dark tab strips.
      const r = 15;
      const cx = size - r - 3;
      const cy = r + 3;
      ctx.beginPath();
      ctx.arc(cx, cy, r + 3, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      if (count <= 9) {
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 26px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(count), cx, cy + 1);
      }
      link.href = canvas.toDataURL('image/png');
    } catch {
      /* canvas taint / encode failure — keep original favicon */
    }
  };
  img.src = faviconBase;
}

/** Restore the original favicon (equivalent to setFaviconBadge(0)). */
export function clearFaviconBadge(): void {
  setFaviconBadge(0);
}
